import React, { useEffect, useRef } from 'react';

import { useIntl } from '../../../util/reactIntl';
import { PrimaryButton } from '../../../components';

import css from './BuyNowButton.module.css';

// Read by the global rule that keeps the Konvers chat bubble clear of this bar
// (marketplaceDefaults.css).
const BAR_HEIGHT_VAR = '--buyBarHeight';

/**
 * "Kjøp nå" / "Buy now" CTA on the ListingPage's right column.
 *
 * Uses the shared `PrimaryButton` so visual style, focus ring, disabled
 * handling, and border-radius stay consistent with the rest of the
 * marketplace.
 *
 * The button is automatically disabled when the listing is closed,
 * when stock is exhausted, or when the viewer is the listing's author.
 * In those cases a short helper line below the button explains *why*,
 * referenced via `aria-describedby` so assistive tech announces the
 * reason alongside the disabled state.
 *
 * On phones and tablets this is a bar fixed to the bottom of the screen, and
 * its height varies: 88px with the button alone, taller with the helper line,
 * taller again if that line wraps. The bar publishes its real height as
 * `--buyBarHeight` on the document while it is mounted, so anything that has
 * to line up with it (the chat bubble) can, without guessing.
 *
 * @param {Object} props
 * @param {Function} props.onBuy
 * @param {boolean} [props.isOwnListing]
 * @param {boolean} [props.isClosed]
 * @param {boolean} [props.isOutOfStock]
 * @returns {JSX.Element}
 */
const BuyNowButton = props => {
  const { onBuy, isOwnListing, isClosed, isOutOfStock } = props;
  const intl = useIntl();

  const disabled = isOwnListing || isClosed || isOutOfStock;

  const labelId = isClosed
    ? 'ListingPage.buyNowClosed'
    : isOutOfStock
    ? 'ListingPage.buyNowOutOfStock'
    : isOwnListing
    ? 'ListingPage.buyNowYourListing'
    : 'ListingPage.buyNow';

  // For sold items the full "Denne varen er solgt" sentence is the button
  // label itself (per client), so there's no separate helper line for that
  // case — only closed / own-listing still explain themselves below.
  const reasonId = isClosed
    ? 'ListingPage.buyNowDisabledReasonClosed'
    : isOwnListing
    ? 'ListingPage.buyNowDisabledReasonOwnListing'
    : null;

  const helperId = 'BuyNowButton-helper';

  const rootRef = useRef(null);
  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return undefined;
    }
    const publish = () => {
      const height = Math.round(node.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty(BAR_HEIGHT_VAR, `${height}px`);
      }
    };
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    if (observer) {
      observer.observe(node);
    }
    return () => {
      if (observer) {
        observer.disconnect();
      }
      document.documentElement.style.removeProperty(BAR_HEIGHT_VAR);
    };
  }, [reasonId]);

  return (
    <div ref={rootRef} className={css.root} data-bottom-bar="buy-now">
      <PrimaryButton
        type="button"
        className={css.button}
        onClick={onBuy}
        disabled={disabled}
        aria-describedby={reasonId ? helperId : undefined}
      >
        {intl.formatMessage({ id: labelId })}
      </PrimaryButton>
      {reasonId ? (
        <p id={helperId} className={css.helper}>
          {intl.formatMessage({ id: reasonId })}
        </p>
      ) : null}
    </div>
  );
};

export default BuyNowButton;
